import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { motion, AnimatePresence } from 'framer-motion';
import { message } from 'antd';
import {
  Plus, Dna, Star, Trash2, Users, UserPlus,
  Search, SlidersHorizontal, ArrowUpDown,
  ChevronLeft, ChevronRight, Clock,
} from 'lucide-react';
import brandDnaActions from '../../../actions/brandDna';
import AppScreenWrapper from '../../../components/app-shell/AppScreenWrapper';
import Sidebar from '../../../components/app-shell/Sidebar';
import Topbar from '../../../components/app-shell/Topbar';
import ScreenOrbs from '../../../components/app-shell/ScreenOrbs';
import useClientData from '../../../hooks/useClientData';

// ── Vivid Glass Design System ─────────────────────────────────────────────────

const V = {
  primary: '#FF7E67',
  accent: '#FF4D8D',
  info: '#38BDF8',
  warning: '#FFD222',
  glass: 'rgba(255,255,255,0.06)',
  glassHover: 'rgba(255,255,255,0.09)',
  glassBorder: 'rgba(255,255,255,0.15)',
  glassBorderHover: 'rgba(255,255,255,0.30)',
  text: '#FFFFFF',
  textSub: '#94A3B8',
  textMuted: '#64748B',
};

const AVATAR_GRADIENTS = [
  'linear-gradient(135deg,#FF7E67,#FF4D8D)',
  'linear-gradient(135deg,#667eea,#764ba2)',
  'linear-gradient(135deg,#38BDF8,#4F3A7D)',
  'linear-gradient(135deg,#FFD222,#FF7E67)',
  'linear-gradient(135deg,#059669,#38BDF8)',
  'linear-gradient(135deg,#FF4D8D,#764ba2)',
];

const avatarGradient = (name = '') =>
  AVATAR_GRADIENTS[(name.charCodeAt(0) || 0) % AVATAR_GRADIENTS.length];

const parseCollaborators = (names) =>
  names ? names.split('||').filter(Boolean) : [];

const PAGE_SIZE = 10;
const FILTER_LABELS = { all: 'Filter', mine: 'Mine', shared: 'Shared with me' };
const SORT_LABELS = { updated: 'Updated', created: 'Created', name: 'A → Z' };

const overlayStyle = {
  position: 'fixed', inset: 0, zIndex: 1000,
  background: 'rgba(0,0,0,0.75)',
  backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
};

const modalStyle = {
  width: '100%', maxWidth: 420, padding: 32, borderRadius: 24,
  background: 'rgba(18,8,30,0.97)',
  border: `1px solid ${V.glassBorder}`,
  backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)',
  boxShadow: '0 24px 80px rgba(0,0,0,0.6)',
  textAlign: 'center',
};

const primaryBtnStyle = {
  padding: '11px 24px', borderRadius: 12, border: 'none',
  background: `linear-gradient(135deg,${V.primary},${V.accent})`,
  color: '#fff', fontSize: 14, fontWeight: 700,
  cursor: 'pointer', fontFamily: 'inherit',
  boxShadow: '0 4px 16px rgba(255,126,103,0.4)',
};

function controlBtnStyle(active) {
  return {
    display: 'flex', alignItems: 'center', gap: 7,
    padding: '9px 14px', borderRadius: 12, cursor: 'pointer',
    background: active ? 'rgba(255,126,103,0.15)' : V.glass,
    border: `1px solid ${active ? V.primary : V.glassBorder}`,
    color: active ? V.primary : V.textSub,
    fontSize: 13, fontFamily: 'inherit',
    backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)',
  };
}

function formatRelative(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(dateStr).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export default function BrandDnaScreen() {
  const navigate = useNavigate();
  const { balance, clientName, clientEmail } = useClientData();
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [sharingBrand, setSharingBrand] = useState(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteSending, setInviteSending] = useState(false);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('updated');
  const [page, setPage] = useState(1);
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [showSortMenu, setShowSortMenu] = useState(false);

  const fetchBrands = useCallback(async () => {
    try {
      setLoading(true);
      const res = await brandDnaActions.listBrands();
      if (res.success) setBrands(res.data);
    } catch {
      message.error('Failed to load brands');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchBrands(); }, [fetchBrands]);
  useEffect(() => { setPage(1); }, [search, filter, sortBy]);

  const confirmDelete = async (brandId) => {
    try {
      await brandDnaActions.deleteBrand(brandId);
      message.success('Brand deleted');
      setDeletingId(null);
      fetchBrands();
    } catch {
      message.error('Failed to delete');
    }
  };

  const handleShare = (e, brand) => {
    e.stopPropagation();
    setSharingBrand(brand);
    setInviteEmail('');
  };

  const handleSendInvite = async () => {
    if (!inviteEmail.trim() || !sharingBrand) return;
    setInviteSending(true);
    try {
      await brandDnaActions.sendBrandInvitation(sharingBrand.brand_id, inviteEmail.trim());
      message.success(`Invitation sent to ${inviteEmail}`);
      setSharingBrand(null);
      setInviteEmail('');
    } catch (err) {
      message.error(err?.response?.data?.error || 'Failed to send invitation');
    } finally {
      setInviteSending(false);
    }
  };

  const filtered = useMemo(() => {
    let list = [...brands];
    if (filter === 'mine') list = list.filter((b) => !b.is_shared);
    if (filter === 'shared') list = list.filter((b) => b.is_shared);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((b) =>
        b.brand_name?.toLowerCase().includes(q) ||
        b.tagline?.toLowerCase().includes(q) ||
        b.industry?.toLowerCase().includes(q)
      );
    }
    if (sortBy === 'name') list.sort((a, b) => a.brand_name.localeCompare(b.brand_name));
    if (sortBy === 'updated') list.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
    if (sortBy === 'created') list.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    return list;
  }, [brands, filter, search, sortBy]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <AppScreenWrapper>
    <div className="screen-root">
      <Sidebar balance={balance} name={clientName} email={clientEmail} />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, position: 'relative' }}>
        <ScreenOrbs tone="purple" />
        <Topbar title="Brand DNA" name={clientName} />
        <div
          style={{ flex: 1, overflowY: 'auto', padding: '32px 24px', fontFamily: 'Inter, sans-serif' }}
          onClick={() => { setShowFilterMenu(false); setShowSortMenu(false); }}
        >
      <div style={{ maxWidth: 1280, margin: '0 auto' }}>
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 32 }}
      >
        <div>
          <h1 style={{ fontSize: 32, fontWeight: 700, color: V.text, margin: 0 }}>Brand DNA</h1>
          <p style={{ fontSize: 14, color: V.textSub, margin: '4px 0 0' }}>
            {loading ? 'Loading…' : `${filtered.length} brand${filtered.length !== 1 ? 's' : ''}`}
          </p>
        </div>
        <motion.button
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
          onClick={() => navigate('/brand-dna/new')}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '10px 20px', borderRadius: 12, border: 'none',
            background: `linear-gradient(135deg,${V.primary},${V.accent})`,
            color: '#fff', fontSize: 14, fontWeight: 700,
            cursor: 'pointer', fontFamily: 'inherit',
            boxShadow: '0 4px 16px rgba(255,126,103,0.35)',
          }}
        >
          <Plus size={16} />
          New Brand
        </motion.button>
      </motion.div>

      {/* Controls */}
      <motion.div
        initial={{ opacity: 0, y: -6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.07 }}
        style={{ display: 'flex', gap: 10, marginBottom: 28, flexWrap: 'wrap', alignItems: 'center' }}
      >
        {/* Search */}
        <div style={{
          flex: 1, minWidth: 200, maxWidth: 360,
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '9px 14px',
          background: V.glass, border: `1px solid ${V.glassBorder}`,
          borderRadius: 12, backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)',
        }}>
          <Search size={15} color={V.textMuted} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search brands…"
            style={{
              flex: 1, background: 'none', border: 'none', outline: 'none',
              color: V.text, fontSize: 14, fontFamily: 'inherit',
            }}
          />
        </div>

        {/* Filter */}
        <div style={{ position: 'relative' }}>
          <motion.button
            whileTap={{ scale: 0.96 }}
            onClick={(e) => { e.stopPropagation(); setShowSortMenu(false); setShowFilterMenu((p) => !p); }}
            style={controlBtnStyle(filter !== 'all')}
          >
            <SlidersHorizontal size={14} />
            {FILTER_LABELS[filter]}
          </motion.button>
          <AnimatePresence>
            {showFilterMenu && (
              <DropdownMenu
                items={[
                  { key: 'all', label: 'All brands' },
                  { key: 'mine', label: 'My brands' },
                  { key: 'shared', label: 'Shared with me' },
                ]}
                active={filter}
                onSelect={(k) => { setFilter(k); setShowFilterMenu(false); }}
              />
            )}
          </AnimatePresence>
        </div>

        {/* Sort */}
        <div style={{ position: 'relative' }}>
          <motion.button
            whileTap={{ scale: 0.96 }}
            onClick={(e) => { e.stopPropagation(); setShowFilterMenu(false); setShowSortMenu((p) => !p); }}
            style={controlBtnStyle(false)}
          >
            <ArrowUpDown size={14} />
            {SORT_LABELS[sortBy]}
          </motion.button>
          <AnimatePresence>
            {showSortMenu && (
              <DropdownMenu
                items={[
                  { key: 'updated', label: 'Recently updated' },
                  { key: 'created', label: 'Recently created' },
                  { key: 'name', label: 'Alphabetical' },
                ]}
                active={sortBy}
                onSelect={(k) => { setSortBy(k); setShowSortMenu(false); }}
              />
            )}
          </AnimatePresence>
        </div>
      </motion.div>

      {/* Grid */}
      {loading ? (
        <div className="brand-grid">
          {[...Array(6)].map((_, i) => <SkeletonCard key={i} />)}
        </div>
      ) : paginated.length === 0 ? (
        <EmptyState hasSearch={!!search || filter !== 'all'} onCreateClick={() => navigate('/brand-dna/new')} />
      ) : (
        <div className="brand-grid">
          <AnimatePresence>
            {paginated.map((brand, idx) => (
              <BrandCard
                key={brand.brand_id}
                brand={brand}
                index={idx}
                onClick={() => navigate(`/brand-dna/${brand.brand_id}`)}
                onShare={(e) => handleShare(e, brand)}
              />
            ))}
          </AnimatePresence>
        </div>
      )}

      {/* Pagination */}
      {!loading && totalPages > 1 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginTop: 36 }}
        >
          <PaginationBtn disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            <ChevronLeft size={16} />
          </PaginationBtn>
          {[...Array(totalPages)].map((_, i) => (
            <motion.button
              key={i}
              whileTap={{ scale: 0.9 }}
              onClick={() => setPage(i + 1)}
              style={{
                width: 34, height: 34, borderRadius: 8,
                background: page === i + 1 ? `linear-gradient(135deg,${V.primary},${V.accent})` : V.glass,
                color: page === i + 1 ? '#fff' : V.textSub,
                fontSize: 13, fontWeight: page === i + 1 ? 700 : 400,
                cursor: 'pointer', fontFamily: 'inherit',
                border: page === i + 1 ? 'none' : `1px solid ${V.glassBorder}`,
              }}
            >
              {i + 1}
            </motion.button>
          ))}
          <PaginationBtn disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
            <ChevronRight size={16} />
          </PaginationBtn>
        </motion.div>
      )}

      {/* Delete Modal */}
      <AnimatePresence>
        {deletingId && (
          <motion.div
            key="delete-overlay"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setDeletingId(null)}
            style={overlayStyle}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              style={modalStyle}
            >
              <div style={{
                width: 52, height: 52, borderRadius: '50%', background: 'rgba(239,68,68,0.12)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px',
              }}>
                <Trash2 size={22} color="#ef4444" />
              </div>
              <h3 style={{ fontSize: 20, fontWeight: 700, color: V.text, margin: '0 0 8px' }}>Delete Brand?</h3>
              <p style={{ fontSize: 14, color: V.textSub, margin: '0 0 24px', lineHeight: 1.6 }}>
                This will permanently remove the brand and all its data. This action cannot be undone.
              </p>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
                <GhostBtn onClick={() => setDeletingId(null)}>Cancel</GhostBtn>
                <motion.button
                  whileTap={{ scale: 0.97 }}
                  onClick={() => confirmDelete(deletingId)}
                  style={{ ...primaryBtnStyle, background: '#ef4444', boxShadow: 'none' }}
                >
                  Delete
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Invite Modal */}
      <AnimatePresence>
        {sharingBrand && (
          <motion.div
            key="invite-overlay"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setSharingBrand(null)}
            style={overlayStyle}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              style={{ ...modalStyle, maxWidth: 440, textAlign: 'left' }}
            >
              <div style={{
                width: 52, height: 52, borderRadius: '50%', background: 'rgba(255,126,103,0.12)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px',
              }}>
                <UserPlus size={22} color={V.primary} />
              </div>
              <h3 style={{ fontSize: 20, fontWeight: 700, color: V.text, margin: '0 0 4px', textAlign: 'center' }}>
                Invite Collaborator
              </h3>
              <p style={{ fontSize: 13, color: V.textSub, margin: '0 0 20px', textAlign: 'center' }}>
                {sharingBrand.brand_name}
              </p>
              <p style={{ fontSize: 13, color: V.textSub, margin: '0 0 10px', lineHeight: 1.6 }}>
                Enter the FluxUro email of the person you want to collaborate with.
              </p>
              <input
                type="email"
                placeholder="colleague@company.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleSendInvite(); }}
                autoFocus
                style={{
                  width: '100%', boxSizing: 'border-box', marginBottom: 20,
                  padding: '11px 14px', borderRadius: 12,
                  background: 'rgba(255,255,255,0.06)',
                  border: `1px solid ${V.glassBorder}`,
                  color: V.text, fontSize: 14, fontFamily: 'inherit', outline: 'none',
                }}
              />
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <GhostBtn onClick={() => setSharingBrand(null)}>Cancel</GhostBtn>
                <motion.button
                  whileTap={{ scale: 0.97 }}
                  onClick={handleSendInvite}
                  disabled={inviteSending || !inviteEmail.trim()}
                  style={{ ...primaryBtnStyle, opacity: !inviteEmail.trim() ? 0.5 : 1, cursor: !inviteEmail.trim() ? 'not-allowed' : 'pointer' }}
                >
                  {inviteSending ? 'Sending…' : 'Send Invitation'}
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      </div>
      <style>{`
        .brand-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 360px));
          gap: 20px;
        }
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }
        input::placeholder { color: #64748B; }
      `}</style>
        </div>{/* scroll container */}
      </div>{/* column */}
    </div>{/* screen-root */}
    </AppScreenWrapper>
  );
}

// ── Helper Components ────────────────────────────────────────

function GhostBtn({ onClick, children }) {
  return (
    <motion.button
      whileTap={{ scale: 0.97 }}
      onClick={onClick}
      style={{
        padding: '10px 22px', borderRadius: 12,
        background: 'transparent', border: `1px solid ${V.glassBorder}`,
        color: V.textSub, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit',
      }}
    >
      {children}
    </motion.button>
  );
}

function PaginationBtn({ onClick, disabled, children }) {
  return (
    <motion.button
      whileTap={!disabled ? { scale: 0.9 } : undefined}
      onClick={onClick}
      disabled={disabled}
      style={{
        width: 34, height: 34, borderRadius: 8,
        background: V.glass, border: `1px solid ${V.glassBorder}`,
        color: disabled ? V.textMuted : V.textSub,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.4 : 1,
      }}
    >
      {children}
    </motion.button>
  );
}

function DropdownMenu({ items, active, onSelect }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: -6, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -6, scale: 0.97 }}
      transition={{ duration: 0.15 }}
      onClick={(e) => e.stopPropagation()}
      style={{
        position: 'absolute', top: '110%', left: 0, zIndex: 200,
        minWidth: 190, borderRadius: 12,
        background: 'rgba(12,6,24,0.98)', border: `1px solid ${V.glassBorder}`,
        backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)',
        boxShadow: '0 16px 40px rgba(0,0,0,0.5)',
        padding: 6,
      }}
    >
      {items.map((item) => (
        <motion.div
          key={item.key}
          whileHover={{ background: 'rgba(255,255,255,0.07)' }}
          onClick={() => onSelect(item.key)}
          style={{
            padding: '9px 14px', borderRadius: 8, cursor: 'pointer',
            color: active === item.key ? V.primary : V.textSub,
            fontSize: 13, fontWeight: active === item.key ? 600 : 400,
            background: active === item.key ? 'rgba(255,126,103,0.1)' : 'transparent',
          }}
        >
          {item.label}
        </motion.div>
      ))}
    </motion.div>
  );
}

function Chip({ color, bg, border, children }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 4,
      padding: '3px 8px', borderRadius: 99,
      background: bg, border: `1px solid ${border}`,
      color, fontSize: 10, fontWeight: 700,
      textTransform: 'uppercase', letterSpacing: '0.08em', whiteSpace: 'nowrap',
    }}>
      {children}
    </div>
  );
}

function CollaboratorCircles({ names }) {
  const visible = names.slice(0, 4);
  const extra = names.length - 4;
  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      {visible.map((name, i) => (
        <Tooltip key={i} text={name}>
          <div
            style={{
              width: 26, height: 26, borderRadius: '50%',
              background: avatarGradient(name),
              border: '2px solid #050505',
              marginLeft: i > 0 ? -9 : 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 10, fontWeight: 800, color: '#fff',
              flexShrink: 0, cursor: 'default',
            }}
          >
            {name.charAt(0).toUpperCase()}
          </div>
        </Tooltip>
      ))}
      {extra > 0 && (
        <Tooltip text={`+${extra} more collaborators`}>
          <div style={{
            width: 26, height: 26, borderRadius: '50%',
            background: V.glass, border: `2px solid #050505`,
            marginLeft: -9,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 9, fontWeight: 700, color: V.textSub, flexShrink: 0, cursor: 'default',
          }}>
            +{extra}
          </div>
        </Tooltip>
      )}
    </div>
  );
}

function Tooltip({ text, children }) {
  const [show, setShow] = useState(false);
  return (
    <div
      style={{ position: 'relative', display: 'inline-flex' }}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      {children}
      {show && (
        <div style={{
          position: 'absolute', bottom: 'calc(100% + 6px)', left: '50%',
          transform: 'translateX(-50%)',
          background: 'rgba(8,4,16,0.97)', border: `1px solid ${V.glassBorder}`,
          color: V.text, fontSize: 11, fontWeight: 500, whiteSpace: 'nowrap',
          padding: '5px 10px', borderRadius: 7, pointerEvents: 'none', zIndex: 500,
          boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
        }}>
          {text}
        </div>
      )}
    </div>
  );
}

function SkeletonCard() {
  return (
    <div style={{
      borderRadius: 24, background: V.glass, border: `1px solid ${V.glassBorder}`,
      padding: 24, height: 230,
      animation: 'pulse 1.6s ease-in-out infinite',
    }}>
      <div style={{ display: 'flex', gap: 14, marginBottom: 16 }}>
        <div style={{ width: 52, height: 52, borderRadius: 14, background: 'rgba(255,255,255,0.08)' }} />
        <div style={{ flex: 1 }}>
          <div style={{ height: 16, width: '60%', background: 'rgba(255,255,255,0.08)', borderRadius: 8, marginBottom: 8 }} />
          <div style={{ height: 11, width: '40%', background: 'rgba(255,255,255,0.05)', borderRadius: 8 }} />
        </div>
      </div>
      <div style={{ height: 13, width: '85%', background: 'rgba(255,255,255,0.06)', borderRadius: 8, marginBottom: 8 }} />
      <div style={{ height: 13, width: '55%', background: 'rgba(255,255,255,0.04)', borderRadius: 8, marginBottom: 22 }} />
      <div style={{ height: 5, background: 'rgba(255,255,255,0.06)', borderRadius: 99 }} />
    </div>
  );
}

// ── Brand Card ──────────────────────────────────────────────

function BrandCard({ brand, index, onClick, onShare }) {
  const [hovered, setHovered] = useState(false);
  const collaborators = parseCollaborators(brand.collaborator_names);
  const score = brand.completion_score || 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ delay: index * 0.05, duration: 0.3 }}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        borderRadius: 24,
        background: hovered ? V.glassHover : V.glass,
        border: `1px solid ${hovered ? V.glassBorderHover : V.glassBorder}`,
        backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)',
        padding: 24, cursor: 'pointer',
        display: 'flex', flexDirection: 'column',
        transition: 'background 0.2s, border-color 0.2s, transform 0.2s, box-shadow 0.2s',
        transform: hovered ? 'translateY(-4px)' : 'none',
        boxShadow: hovered ? '0 16px 40px rgba(0,0,0,0.3)' : '0 2px 12px rgba(0,0,0,0.15)',
        position: 'relative',
        minHeight: 220,
      }}
    >
      {/* Row 1: logo/avatar + name + chips */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, marginBottom: 14 }}>
        {brand.logo_url ? (
          <img
            src={brand.logo_url}
            alt={brand.brand_name}
            style={{
              width: 52, height: 52, borderRadius: 14, flexShrink: 0,
              objectFit: 'contain', background: 'rgba(255,255,255,0.05)',
              border: `1px solid ${V.glassBorder}`,
            }}
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
        ) : null}
        <div style={{
          width: 52, height: 52, borderRadius: 14, flexShrink: 0,
          background: avatarGradient(brand.brand_name),
          display: brand.logo_url ? 'none' : 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 20, fontWeight: 800, color: '#fff',
          boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
        }}>
          {(brand.brand_name || '?').charAt(0).toUpperCase()}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h2 style={{
            fontSize: 17, fontWeight: 600, color: V.text, margin: '0 0 4px',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {brand.brand_name}
          </h2>
          {brand.industry && (
            <span style={{ fontSize: 11, fontWeight: 500, color: V.info, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              {brand.industry}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-end', flexShrink: 0 }}>
          {brand.is_shared ? (
            <Chip color="#fa8c16" bg="rgba(250,140,22,0.12)" border="rgba(250,140,22,0.25)">
              <Users size={9} />Shared
            </Chip>
          ) : brand.is_default ? (
            <Chip color={V.primary} bg="rgba(255,126,103,0.12)" border="rgba(255,126,103,0.25)">
              <Star size={9} fill={V.primary} />Default
            </Chip>
          ) : null}
          {brand.status === 'extracting' && (
            <Chip color={V.warning} bg="rgba(255,210,34,0.1)" border="rgba(255,210,34,0.25)">
              Analyzing…
            </Chip>
          )}
        </div>
      </div>

      {/* Description */}
      <p style={{
        fontSize: 13, color: V.textSub, margin: '0 0 16px',
        lineHeight: 1.55, fontStyle: 'italic',
        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
        overflow: 'hidden', minHeight: 40,
      }}>
        {brand.tagline ? `\u201c${brand.tagline}\u201d` : '\u00a0'}
      </p>

      {/* DNA Strength */}
      <div style={{ marginBottom: 16 }}>
        {score >= 100 ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Chip color={V.warning} bg="rgba(255,210,34,0.12)" border="rgba(255,210,34,0.3)">
              ✦ DNA Complete
            </Chip>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <span style={{ fontSize: 10, fontWeight: 900, color: V.warning, textTransform: 'uppercase', letterSpacing: '1.5px' }}>
                DNA Strength
              </span>
              <span style={{ fontSize: 12, fontWeight: 700, color: V.warning }}>{score}%</span>
            </div>
            <div style={{ height: 5, background: 'rgba(0,0,0,0.4)', borderRadius: 99, overflow: 'hidden' }}>
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${score}%` }}
                transition={{ duration: 0.8, delay: index * 0.05 + 0.2, ease: 'easeOut' }}
                style={{ height: '100%', background: V.warning, borderRadius: 99, boxShadow: `0 0 8px ${V.warning}` }}
              />
            </div>
          </>
        )}
      </div>

      <div style={{ flex: 1 }} />

      {/* Footer */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        paddingTop: 14, borderTop: `1px solid ${V.glassBorder}`,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, color: V.textMuted, fontSize: 11 }}>
          <Clock size={11} />
          {formatRelative(brand.updated_at || brand.created_at)}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {collaborators.length > 0 && <CollaboratorCircles names={collaborators} />}
          {!brand.is_shared && (
            <Tooltip text="Add collaborator">
              <motion.div
                whileHover={{ background: 'rgba(102,126,234,0.18)' }}
                whileTap={{ scale: 0.9 }}
                onClick={(e) => { e.stopPropagation(); onShare(e); }}
                style={{
                  width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
                  marginLeft: collaborators.length > 0 ? -9 : 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: 'rgba(102,126,234,0.08)',
                  border: '2px dashed rgba(102,126,234,0.5)',
                  cursor: 'pointer',
                }}
              >
                <UserPlus size={11} color="#667eea" />
              </motion.div>
            </Tooltip>
          )}
        </div>
      </div>
    </motion.div>
  );
}

// ── Empty State ─────────────────────────────────────────────

function EmptyState({ hasSearch, onCreateClick }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      style={{
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        padding: '80px 24px', textAlign: 'center',
      }}
    >
      <motion.div
        animate={{ rotate: [0, 5, -5, 0] }}
        transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
        style={{
          width: 80, height: 80, borderRadius: 24,
          background: 'linear-gradient(135deg,rgba(255,126,103,0.12),rgba(255,77,141,0.12))',
          border: '1px solid rgba(255,126,103,0.2)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          marginBottom: 24,
        }}
      >
        <Dna size={36} color={V.primary} />
      </motion.div>

      <h2 style={{ fontSize: 24, fontWeight: 700, color: V.text, margin: '0 0 10px' }}>
        {hasSearch ? 'No brands match' : 'No brands yet'}
      </h2>
      <p style={{ fontSize: 14, color: V.textSub, maxWidth: 380, lineHeight: 1.6, margin: '0 0 28px' }}>
        {hasSearch
          ? 'Try a different search term or filter.'
          : 'Paste your website URL and let AI extract your brand identity, voice, audience, and visual style — in seconds.'}
      </p>

      {!hasSearch && (
        <motion.button
          whileHover={{ scale: 1.04 }}
          whileTap={{ scale: 0.97 }}
          onClick={onCreateClick}
          style={{ ...primaryBtnStyle, display: 'flex', alignItems: 'center', gap: 8 }}
        >
          <Plus size={18} />
          Create Your First Brand DNA
        </motion.button>
      )}
    </motion.div>
  );
}
